import PropTypes from 'prop-types';
import { BsEnvelope, BsPeopleFill } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import SectionCard from '#globalComponents/Card/SectionCard';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import EmptyState from '#globalComponents/EmptyState/EmptyState';
import { getGermanLabel } from '#lib/constants/contactRoles';

import { JOB_TYPE_NAMES } from '../../constants/jobTypeNames';
import { useCompanyContactEnrichment } from '../../hooks/useCompanyContactEnrichment';
import ContactPhone, { hasLockedPhone } from '../ContactPhone/ContactPhone';
import JobProgressBar from '../LeadOverviewPage/JobProgressBar/JobProgressBar';

const ContactRow = ({ contact, isSelected, onToggle, lockedPhoneCount }) => (
	<li
		onClick={onToggle}
		className={`flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors ${
			isSelected ? 'bg-primary-100' : 'hover:bg-bgBase-300'
		}`}
	>
		<div onClick={(e) => e.stopPropagation()}>
			<Checkbox id={`company-contact-${contact.id}`} checked={isSelected} onChange={onToggle} />
		</div>
		<div className="min-w-0 flex-1">
			<div className="text-sm font-medium text-text">{contact.fullname}</div>
			{contact.companyRole && (
				<div className="mt-0.5 text-xs text-bgBase-800">{getGermanLabel(contact.companyRole)}</div>
			)}
			{contact.emailAddress && (
				<div className="mt-0.5 flex items-center gap-1 text-xs text-text">
					<BsEnvelope className="h-3 w-3 flex-shrink-0" />
					<span className="truncate">{contact.emailAddress}</span>
				</div>
			)}
			<ContactPhone contact={contact} lockedCount={lockedPhoneCount} className="mt-0.5" />
		</div>
	</li>
);

ContactRow.propTypes = {
	contact: PropTypes.object.isRequired,
	isSelected: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
	lockedPhoneCount: PropTypes.number.isRequired,
};

const NoContacts = ({ companyId, prospectQueryId }) => {
	const { enrichmentJob, isStarting, startEnrichment } = useCompanyContactEnrichment(companyId, prospectQueryId);

	if (enrichmentJob) {
		return (
			<div className="p-4">
				<JobProgressBar job={enrichmentJob} jobTypeName={JOB_TYPE_NAMES.contactEnrichment} />
			</div>
		);
	}

	return (
		<EmptyState
			icon={BsPeopleFill}
			title="Keine Ansprechpartner gefunden"
			action={
				// Enrichment is tied to a search query — a stale direct URL has none to enrich into.
				prospectQueryId && (
					<Button size="sm" loading={isStarting} onClick={startEnrichment}>
						{isStarting ? 'Anreicherung wird gestartet …' : 'Ansprechpartner anreichern'}
					</Button>
				)
			}
		/>
	);
};

NoContacts.propTypes = {
	companyId: PropTypes.string.isRequired,
	prospectQueryId: PropTypes.string,
};

const CompanyContacts = ({ contacts, companyId, prospectQueryId, selectedContactIds, setSelectedContactIds }) => {
	const contactsWithEmailCount = contacts.filter((c) => c.emailAddress).length;
	const lockedPhoneCount = contacts.filter(hasLockedPhone).length;
	const allSelected = contacts.length > 0 && selectedContactIds.length === contacts.length;

	const toggleContact = (contactId) => {
		setSelectedContactIds((prev) =>
			prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId],
		);
	};

	const toggleAll = () => setSelectedContactIds(allSelected ? [] : contacts.map((c) => c.id));

	return (
		<SectionCard
			title="Ansprechpartner"
			subtitle={`${contacts.length} Kontakte · ${contactsWithEmailCount} mit E-Mail`}
			icon={BsPeopleFill}
			bodyClassName="p-0"
			headerAction={
				contacts.length > 0 && (
					<Checkbox
						id="company-contacts-select-all"
						checked={allSelected}
						showDash={selectedContactIds.length > 0 && !allSelected}
						onChange={toggleAll}
						label="Alle"
					/>
				)
			}
		>
			{contacts.length === 0 ? (
				<NoContacts companyId={companyId} prospectQueryId={prospectQueryId} />
			) : (
				<ul className="max-h-96 divide-y divide-bgBase-500 overflow-y-auto">
					{contacts.map((contact) => (
						<ContactRow
							key={contact.id}
							contact={contact}
							isSelected={selectedContactIds.includes(contact.id)}
							onToggle={() => toggleContact(contact.id)}
							lockedPhoneCount={lockedPhoneCount}
						/>
					))}
				</ul>
			)}
		</SectionCard>
	);
};

CompanyContacts.propTypes = {
	contacts: PropTypes.array.isRequired,
	companyId: PropTypes.string.isRequired,
	prospectQueryId: PropTypes.string,
	selectedContactIds: PropTypes.array.isRequired,
	setSelectedContactIds: PropTypes.func.isRequired,
};

export default CompanyContacts;
